import { DateStr, toDateStr } from '@mi-front/core';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import { Field } from 'formik';
import { FieldProps } from 'formik/dist/Field';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { SingleDatePicker, SingleDatePickerShape } from 'react-dates';
import 'react-dates/initialize';
import { InputProps } from '../../base';
import { DecoratedFormUnit } from '../../base/DecoratedFormUnit';

const StyledWrapper = styled('div')(({ theme }) => ({
  display: 'inline-block',
  width: '100%',
  position: 'relative',

  '& .SingleDatePickerInput, & .DateRangePickerInput': {
    borderRadius: '0.375rem', // $border-radius
  },

  '& .SingleDatePicker, & .SingleDatePickerInput': {
    width: '100%',
    textAlign: 'center',
  },

  '&.date-pick-sm, &.date-range-sm': {
    '& .DateRangePickerInput .DateInput': {
      width: '140px',
    },
    '& .SingleDatePickerInput, & .DateRangePickerInput': {
      borderRadius: '0.2rem', // $border-radius-sm
    },
    '& .SingleDatePickerInput_calendarIcon, & .DateRangePickerInput_calendarIcon': {
      padding: '6px',
    },
    '& .DateInput_input': {
      fontSize: '0.875rem', // $font-size-sm
      paddingTop: '4px',
      paddingBottom: '2px',
    },
    '& .DateRangePickerInput_arrow_svg': {
      width: '18px',
      height: '18px',
      marginTop: '-4px',
    },
  },

  '& .DateRangePickerInput__withBorder': {
    borderColor: `${theme.palette.grey[400]} !important`,
  },

  '& .DateInput': {
    borderRadius: '0.45rem', // $border-radius-lg
    color: theme.palette.grey[800],
    '& ::placeholder': {
      color: theme.palette.grey[500],
      opacity: 1,
    },
  },

  '& .DateRangePickerInput .DateInput': {
    width: '150px',
  },

  '& .DateInput_input': {
    borderRadius: '0.375rem', // $border-radius
    textAlign: 'center',
    fontSize: '1rem', // $font-size-base
    paddingTop: '0.5rem', // $input-btn-padding-y
    paddingBottom: '6px',
  },

  '& .DateInput_input__focused': {
    borderBottomColor: theme.palette.primary.main,
    borderBottomLeftRadius: '2px',
    borderBottomRightRadius: '2px',
  },

  '& .DateInput_input__disabled': {
    color: theme.palette.grey[500],
  },

  // NOTE: the order of these styles DO matter
  '& .CalendarDay__selected_span': {
    background: '#92e1fe', // $color-AquaLight
    color: theme.palette.grey[700],
    border: `1px solid ${theme.palette.grey[100]}`,
    '&:active, &:hover': {
      background: theme.palette.info.main,
      border: `1px double ${theme.palette.info.main}`,
      color: '#fff',
    },
  },

  '& .CalendarDay__selected': {
    background: theme.palette.info.main,
    color: theme.palette.common.white,
    '&:hover': {
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },

  '& .CalendarDay__hovered_span, & .CalendarDay__hovered_span:hover': {
    background: theme.palette.grey[200],
  },
}));

type Props = Omit<InputProps, 'onChange' | 'css'> & {
  isOutsideRange?: SingleDatePickerShape['isOutsideRange'];
  value?: null | string | DateStr;
  onChange?: (date: null | DateStr) => void;
  size?: 'sm';
  showIcon?: boolean;
  /** When true, prevents opening the calendar (readonly). */
  readOnly?: boolean;
};

export function DateInputP(p: Props) {
  const { size, value, onChange, isOutsideRange, showIcon, name, readOnly, ...rest } = p;
  const [focused, setFocused] = useState<boolean>(false);

  return (
    <DecoratedFormUnit {...rest}>
      <StyledWrapper className={classNames('date-pick', p.size && `date-pick-${size}`)}>
        <SingleDatePicker
          disabled={rest.disabled}
          id={name || 'date_input'}
          date={value ? moment(value) : (null as any)}
          onDateChange={(date) => onChange && onChange(date && toDateStr(date as any))}
          focused={readOnly ? false : focused}
          onFocusChange={({ focused: nextFocused }) => !readOnly && setFocused(nextFocused)}
          isOutsideRange={isOutsideRange || (() => false)}
          numberOfMonths={1}
          hideKeyboardShortcutsPanel
          showDefaultInputIcon={showIcon}
        />
      </StyledWrapper>
    </DecoratedFormUnit>
  );
}

DateInputP.defaultProps = {
  showIcon: true,
};

export const DateInput = DateInputP;

export function DateField(props: React.ComponentProps<typeof DateInput> & { name: string }) {
  const { name, onChange, ...rest } = props;

  return (
    <Field name={name}>
      {(fp: FieldProps) => {
        return (
          <DateInput
            {...fp.field}
            {...rest}
            onChange={
              onChange ||
              ((date) => {
                fp.form.setFieldValue(name, date);
              })
            }
            name={name}
            error={fp.meta.touched && fp.meta.error}
            disabled={rest.disabled}
            readOnly={fp.form.isSubmitting}
          />
        );
      }}
    </Field>
  );
}
